import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaExchangeAlt, FaLeaf, FaSeedling, FaInfoCircle } from 'react-icons/fa';
import MiniChart from './MiniChart';

const PlotItem = ({ plot }) => {
  const { 
    name, 
    location, 
    icon, 
    value, 
    chartColor, 
    metricType = 'temperature',
    rotationStatus,
    currentCrop,
    nextRecommendedCrop,
    biodiversityIndex
  } = plot;
  
  const [expanded, setExpanded] = useState(false);
  
  // Determine status color based on rotation status
  const getRotationStatusColor = () => {
    if (!rotationStatus) return 'var(--text-secondary)';
    
    if (rotationStatus === 'Optimal') return 'var(--accent-green)';
    if (rotationStatus === 'Needs Rotation') return 'var(--accent-yellow)';
    if (rotationStatus === 'Critical') return 'var(--accent-red)';
    
    return 'var(--text-secondary)';
  };
  
  // Determine biodiversity status
  const getBiodiversityStatus = () => {
    if (!biodiversityIndex) return { text: 'Unknown', color: 'var(--text-secondary)' };
    
    if (biodiversityIndex >= 80) return { text: 'Excellent', color: 'var(--accent-green)' };
    if (biodiversityIndex >= 70) return { text: 'Good', color: 'var(--accent-green)' };
    if (biodiversityIndex >= 60) return { text: 'Moderate', color: 'var(--accent-yellow)' };
    return { text: 'Needs Improvement', color: 'var(--accent-red)' };
  };
  
  const biodiversityStatus = getBiodiversityStatus();
  
  return (
    <div className={`plot-item ${expanded ? 'expanded' : ''}`}>
      <div className="plot-header" onClick={() => setExpanded(!expanded)}>
        <div>
          <div className="plot-name">
            <span className="plot-icon">{icon}</span>
            {name}
          </div>
          <div className="plot-location">{location}</div>
        </div>
        <div className="metric-value">{value}</div>
      </div>
      <div className="chart-container">
        <MiniChart 
          color={chartColor} 
          metricType={metricType} 
          useDMVData={true}
          height={expanded ? 60 : 40}
          showTooltip={true}
        />
      </div>
      <div className="plot-footer" onClick={() => setExpanded(!expanded)}>
        {expanded ? <FaChevronUp /> : <FaChevronDown />}
      </div>
      
      {expanded && (
        <div className="plot-details">
          <div className="plot-detail-row">
            <div className="plot-detail-label">
              <FaExchangeAlt /> Crop Rotation
            </div>
            <div className="plot-detail-value">
              <div className="rotation-status" style={{ color: getRotationStatusColor() }}>
                {rotationStatus || 'Unknown'}
              </div>
              <div className="crop-info">
                <span className="current-crop">
                  <FaSeedling /> Current: {currentCrop || 'Unknown'}
                </span>
                <span className="next-crop">
                  <FaLeaf /> Next: {nextRecommendedCrop || 'Unknown'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="plot-detail-row">
            <div className="plot-detail-label">
              <FaLeaf /> Biodiversity Index
              <FaInfoCircle className="info-icon" title="Measure of species diversity in the farm area" />
            </div>
            <div className="plot-detail-value">
              <div className="biodiversity-meter">
                <div 
                  className="biodiversity-fill" 
                  style={{ 
                    width: `${biodiversityIndex || 0}%`,
                    backgroundColor: biodiversityStatus.color
                  }}
                ></div>
              </div>
              <div className="biodiversity-value" style={{ color: biodiversityStatus.color }}>
                {biodiversityIndex || 0}/100 - {biodiversityStatus.text}
              </div>
            </div>
          </div>
          
          <div className="plot-recommendations">
            <strong>Recommendations:</strong>
            <ul>
              {rotationStatus === 'Needs Rotation' && (
                <li>Rotate to {nextRecommendedCrop} within the next 2 weeks</li>
              )}
              {rotationStatus === 'Critical' && (
                <li>Immediate rotation to {nextRecommendedCrop} required</li>
              )}
              {biodiversityIndex < 70 && (
                <li>Introduce companion plants to increase biodiversity</li>
              )}
              {biodiversityIndex < 60 && (
                <li>Consider implementing hedgerows and buffer zones</li>
              )}
              <li>Monitor soil health regularly</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotItem; 